import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  LEDGER_CHANNELS,
  LEDGER_FILTERS,
  LEDGER_FILTER_LABELS,
  type LedgerFilter,
  type LedgerChannel,
} from '@marva/shared/ledger-vocabulary'
import { useWorkspaceStore } from '../stores/workspaceStore'
import { useAskEntity, useContextPanel, useDrawerPresence, useOpenAsk } from '../shell/ShellContext'
import { activityApi, type Activity, type ActivityFeedback, type LedgerCampaignDetail, type LedgerItem } from '../services/api'
import { fmtCurrency, isDecisionEntry, receiptOf, type DisplayActivity } from '../lib/activityNarrative'
import StoryDrawer, {
  type StoryPlaceholder,
  LEDGER_CHANNEL_LABELS,
  campaignSkipAnchor,
  channelLabel,
  conversationSkipped,
  isGoldChip,
  isLedgerCampaign,
  isMerchantAction,
  isReplyDetail,
  sentTimes,
  storyTitle,
} from '../components/decision/StoryDrawer'
import {
  Badge,
  Button,
  Chip,
  EmptyState,
  Eyebrow,
  EntryList,
  EntryRow,
  ErrorState,
  FilterBar,
  FilterMenu,
  LoadingState,
  LoadMore,
  PageHeader,
  Section,
} from '../ds'
import AttributionStrip from '../components/ledger/AttributionStrip'
import LedgerBanners from '../components/ledger/LedgerBanners'
import WorthALook from '../components/ledger/WorthALook'
import type { FeedbackState } from '../components/decision/DecisionFeedback'
import {
  groupByDayPart,
  ledgerTimeZone,
  ledgerWhy,
  listHeading,
  rowStatus,
  windowFrom,
  WINDOW_WORDS,
  type AttributionWindow,
} from '../components/ledger/ledgerLines'

/**
 * The Ledger — the first v5 surface (MAR-110 phase 4b). One line per decision, newest first,
 * read from `GET /activity?view=ledger` (docs/reference/api/governance.md). Design reference:
 * the v6 prototype's Ledger screen; what ships and what does not is docs/work/app-ia-v5.md §10.
 *
 * ⚠️ Every piece of retained state names the workspace (and, for the list, the query) it was
 * read under, and is checked WHERE IT IS READ — never cleared in an effect, which permits one
 * render of workspace A's decisions under workspace B (docs/work/app-ia-v5.md §4).
 *
 * Above "The night" (MAR-309, when Ledger absorbed Today): the attribution strip and its window
 * (`components/ledger/AttributionStrip`, its own period read), then the Shadow and "waiting on
 * you" banners (`components/ledger/LedgerBanners`, the shell's one `useNeedsYou` queue). Each
 * reads a complete server read, never a count over this page's rows.
 *
 * MAR-316 (the v6 review's quick wins): the window is the page's, so the list heading follows it
 * ("The night" for 24h only); the lines are cut by day part (`ledgerLines.groupByDayPart`); a row
 * states a status only when it is not the default send, and carries no raw type or timing token.
 * The window BOUNDS the list (the route's `from`, fixed once per read and reused by every "Show
 * more", since the route refuses a cursor outside its range), so the heading is true: an empty
 * window says "Nothing in the last 7 days" and claims nothing about what lies further back.
 */

/** Page size. The API clamps at 100; 25 keeps a first paint quick. */
export const LEDGER_PAGE_SIZE = 25

/**
 * How long a settled "Was this the right call?" answer can be undone before it is sent. The
 * feedback route has no delete (a second answer replaces the first), so Undo exists only BEFORE
 * the POST: the answer is held this long, and sent at once if the drawer closes or moves on.
 */
export const FEEDBACK_UNDO_MS = 6000

/**
 * The ids the drawer will READ. `?decision=` comes from the URL, so it is untrusted: anything
 * outside the server's own decision-id charset (`dec_…`, `rep_…`, or an older row's bare uuid —
 * letters, digits, `_` and `-`) is the honest not-found state, and no request is made.
 */
const READABLE_ID = /^[A-Za-z0-9_-]{1,128}$/
/**
 * A campaign line's id (MAR-246): `grp_` + base64url of its key, then ONE `.` and the page's
 * anchor decision id — `grp_<key>.<anchor>`. Longer than a decision id. Exactly one dot, and only
 * between two non-empty runs of the decision-id charset: never `/`, `%`, or a `..` segment.
 */
const CAMPAIGN_ID = /^grp_[A-Za-z0-9_-]{1,1020}(?:\.[A-Za-z0-9_-]{1,128})?$/
export function isReadableId(id: string | null | undefined): id is string {
  return typeof id === 'string' && (READABLE_ID.test(id) || CAMPAIGN_ID.test(id))
}
function isCampaignId(id: string | null | undefined): boolean {
  return typeof id === 'string' && CAMPAIGN_ID.test(id)
}

/** A row the server could not read and gave no id: there is nothing to open. */
function rowId(item: LedgerItem): string | null {
  return isReadableId(item.id) ? item.id : null
}

/** The receipt classes whose drawer links to Authority's limits (a bound was the story). */
const LIMIT_CLASSES = new Set(['help_blocked', 'escalate'])

type ListState = {
  workspaceId: string
  /** The query this list answers — the window, the filter words and channels, joined. */
  key: string
  /** The window's lower bound this read was made with; every further page reuses it. */
  from: string
  status: 'loading' | 'ready' | 'error'
  items: LedgerItem[]
  cursor: string | null
  hasMore: boolean
  /** The LAST page read ran out of read budget before it filled. */
  partial: boolean
  /** The last page came back with nothing, though there is more to read. */
  lastPageEmpty: boolean
  more: 'idle' | 'loading' | 'error'
}

type DetailState = {
  workspaceId: string
  id: string
  status: 'loading' | 'ready' | 'not_found' | 'error'
  data: Activity | null
}

/** A row's time, in the Ledger's timezone. Its day is the day-part divider above it. */
function fmtRowTime(ts: string, timeZone?: string): string | null {
  const d = new Date(ts)
  if (isNaN(d.getTime())) return null
  return d.toLocaleTimeString('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hour12: false })
}

/** A campaign line (MAR-246): one title for many people, a chip that counts them. */
function isCampaignLine(item: LedgerItem): boolean {
  return item.source === 'campaign'
}

/**
 * A short-cluster rollup (MAR-387) — campaign-shaped, so it opens the campaign drawer. Its time is
 * the cluster's span ("09:15–09:19") and its open cue counts the lines it folds.
 */
function rollupTime(item: LedgerItem, timeZone?: string): string | null {
  if (!item.rollup || !item.timestampFrom) return null
  const from = fmtRowTime(item.timestampFrom, timeZone)
  const to = fmtRowTime(item.timestamp, timeZone)
  if (!from || !to) return null
  return from === to ? to : `${from}–${to}`
}

/** The one sentence under a rolled-up chat line: why the Ledger, not she, titled it. */
const ANONYMOUS_CHAT_ROLLUP_WHY = 'None of them left a name, or a conversation she could title.'

/** What the drawer is titled, and the Ask pin labelled — one string, so the two cannot differ. */
function drawerTitle(row: LedgerItem | null, detail: Activity | null): string {
  if (row?.headline) return row.headline
  // The detail route carries the row's own headline, so a deep-linked drawer is titled (and
  // pinned) exactly as its row would be.
  if (detail?.headline) return detail.headline
  // A reply has no subject or type to title it by: its own headline is the only name it has.
  if (isReplyDetail(detail)) return detail.headline || 'Reply'
  if (detail) return storyTitle(detail as DisplayActivity) || 'Decision'
  return 'Decision'
}

// ── Filters ────────────────────────────────────────────────────────────────────────────────

/** Keep the menu's own order, whatever order the merchant ticked in. */
function inMenuOrder<T extends string>(options: readonly { value: T }[], next: string[]): T[] {
  return options.map((o) => o.value).filter((o) => next.includes(o))
}

const ACTION_OPTIONS = LEDGER_FILTERS.map((f) => ({ value: f, label: LEDGER_FILTER_LABELS[f] }))
const CHANNEL_OPTIONS = LEDGER_CHANNELS.map((c) => ({ value: c, label: LEDGER_CHANNEL_LABELS[c] }))

// ── The row ────────────────────────────────────────────────────────────────────────────────

function LedgerRow({
  item,
  onOpen,
  timeZone,
}: {
  item: LedgerItem
  onOpen: (item: LedgerItem) => void
  timeZone?: string
}) {
  const time = rollupTime(item, timeZone) ?? fmtRowTime(item.timestamp, timeZone)
  // An unreadable row with no id is a line, not a door: nothing to open, nothing to pin.
  const openable = rowId(item) != null
  const campaign = isCampaignLine(item)
  // Deduped by LABEL: two raw channels that read the same are one chip (and one React key).
  const channels = [
    ...new Set(
      campaign && item.channels && item.channels.length > 0
        ? item.channels.map((c) => channelLabel(c)).filter((c): c is string => !!c)
        : [channelLabel(item.channel)].filter((c): c is string => !!c)
    ),
  ]
  const skipped = !item.unreadable && conversationSkipped(item)
  const why = item.rollup === 'anonymous_chat' ? ANONYMOUS_CHAT_ROLLUP_WHY : ledgerWhy(item)
  const revenue = typeof item.revenue === 'number' && item.revenue > 0 ? item.revenue : null
  // One status, and only when it is not the default send (finding 04). No raw type, no raw timing
  // token (`delay_24h`): the channel chip already says where, and the drawer says when.
  const status = rowStatus(item, isGoldChip)
  const reply = isReplyDetail(item)
  // A conversation line stands for several replies; a send that went out more than once says so.
  const turns = !campaign && typeof item.turns === 'number' && item.turns > 1 ? item.turns : null
  const times = reply ? sentTimes(item.sendCount) : null
  const openLabel = item.rollup
    ? `Open all ${item.turns ?? item.memberIds?.length ?? 0} →`
    : isMerchantAction(item)
      ? 'What changed →'
      : reply
        ? 'What she sent →'
        : 'Why she did this →'
  const hasChips = !!status || channels.length > 0 || !!times || item.deliveryStatus === 'queued'
  const chips = hasChips ? (
    <>
      {status && (
        <Chip
          tone={status.tone}
          dot={status.choseNot}
          data-testid={status.choseNot ? 'ledger-chose-not' : 'ledger-status'}
        >
          {status.label}
        </Chip>
      )}
      {channels.map((c) => (
        <Chip key={c}>{c}</Chip>
      ))}
      {times && (
        <Chip tone="gold" data-testid="ledger-sent-times">
          {times}
        </Chip>
      )}
      {item.deliveryStatus === 'queued' && <Chip>Queued for her next visit</Chip>}
    </>
  ) : undefined
  const validTime = !isNaN(new Date(item.timestamp).getTime())
  // One target per line (EntryRow): a <button> when there is a story to open; an unreadable
  // row with no id is a line, not a door — a static row with no open cue.
  return (
    <EntryRow
      testId="ledger-row"
      data-id={item.id ?? undefined}
      onOpen={openable ? () => onOpen(item) : undefined}
      when={validTime && time ? time : undefined}
      dateTime={validTime ? item.timestamp : undefined}
      title={<span data-testid="ledger-headline">{item.headline}</span>}
      tag={
        turns ? (
          <Badge mono data-testid="ledger-turns">
            {turns} replies
          </Badge>
        ) : undefined
      }
      clamp={false}
      why={
        why || skipped ? (
          <>
            {why && (
              <span className="mv-clamp-1" data-testid="ledger-why">
                {why}
              </span>
            )}
            {skipped && (
              <span data-testid="ledger-why">She didn’t look up the conversation for this one — open it to see it.</span>
            )}
          </>
        ) : undefined
      }
      chips={chips}
      value={revenue != null ? <span data-testid="ledger-value">{fmtCurrency(revenue)}</span> : undefined}
      valueLabel={revenue != null ? 'attributed to this decision' : undefined}
      openLabel={openLabel}
    />
  )
}

// ── The screen ─────────────────────────────────────────────────────────────────────────────

export default function Ledger() {
  const { currentWorkspace } = useWorkspaceStore()
  const wsId = currentWorkspace?.id
  const isShadow = currentWorkspace?.mode === 'shadow'
  const [searchParams, setSearchParams] = useSearchParams()
  const paramId = searchParams.get('decision')

  useContextPanel(null, [])

  // The window the attribution strip reads and the list is headed by (MAR-316).
  const [period, setPeriod] = useState<AttributionWindow>('24h')
  // ⚠️ The day-part dividers read the BROWSER's zone; a campaign line's night is cut server-side
  // in the WORKSPACE's (notification prefs / workspace timezone — activity-feed
  // `resolveLedgerTimezone`). When the two differ, a campaign line sits under the divider of its
  // newest member's local time here, while its members are one night in the workspace's zone.
  // Accepted: the divider is where the merchant reads it, the line is how her day was counted.
  const timeZone = useMemo(() => ledgerTimeZone(), [])

  const [filter, setFilter] = useState<LedgerFilter[]>([])
  const [channel, setChannel] = useState<LedgerChannel[]>([])
  const queryKey = `${period}|${filter.join(',')}|${channel.join(',')}`
  const filtered = filter.length > 0 || channel.length > 0

  // ── The list ─────────────────────────────────────────────────────────────────
  const [list, setList] = useState<ListState | null>(null)
  const [reload, setReload] = useState(0)
  // Bumped by every read; a response that is not the newest read writes nothing.
  const listSeq = useRef(0)
  // The workspace whose list has been read at least once (ready OR failed). The opening beat is
  // for the FIRST read of a workspace only; a filter change, a channel change or "Try again"
  // re-reads under the inline loader rather than collapsing the page into a 62vh orb and
  // announcing "Replaying the night" for a filter. Scoped at read like everything else here: a
  // switch to another workspace is a first read again.
  const [landedFor, setLandedFor] = useState<string | null>(null)

  useEffect(() => {
    if (!wsId) return
    const seq = ++listSeq.current
    const key = queryKey
    const from = windowFrom(period, new Date())
    setList({
      workspaceId: wsId,
      key,
      from,
      status: 'loading',
      items: [],
      cursor: null,
      hasMore: false,
      partial: false,
      lastPageEmpty: false,
      more: 'idle',
    })
    activityApi
      .ledger(wsId, { filter, channel, from, limit: LEDGER_PAGE_SIZE })
      .catch(() => null)
      .then((res) => {
        if (seq !== listSeq.current) return
        setLandedFor(wsId)
        if (res?.success && res.data && Array.isArray(res.data.items)) {
          const page = res.data
          setList({
            workspaceId: wsId,
            key,
            from,
            status: 'ready',
            items: page.items,
            cursor: page.cursor,
            hasMore: !!page.hasMore,
            partial: !!page.partial,
            lastPageEmpty: page.items.length === 0 && !!page.hasMore,
            more: 'idle',
          })
        } else {
          // A failed read is an ERROR, never an empty ledger.
          setList((prev) => (prev && prev.workspaceId === wsId && prev.key === key ? { ...prev, status: 'error' } : prev))
        }
      })
    // `period`/`filter`/`channel` are captured through `queryKey`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, queryKey, reload])

  // Scoped at the read: a list read under another workspace or another query is not this one.
  const view = list && wsId && list.workspaceId === wsId && list.key === queryKey ? list : null
  const status: ListState['status'] = view ? view.status : 'loading'
  const items = useMemo(() => view?.items ?? [], [view])

  // A re-read (window, filter, channel, Try again) swaps the list for an inline loader, which
  // collapsed the page and made the canvas's scroll jump (MAR-412). The page keeps the height it
  // last settled at until the new read lands; a first read of a workspace is the opening beat and
  // holds nothing.
  const rereading = status === 'loading' && landedFor === wsId
  const pageRef = useRef<HTMLDivElement>(null)
  const [settledHeight, setSettledHeight] = useState<number | null>(null)
  // Every settled render, not only when `rereading` flips: "Show more" and Worth a look landing
  // grow the page too. The equality check ends the chain.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    if (rereading || !pageRef.current) return
    const h = pageRef.current.offsetHeight
    setSettledHeight((prev) => (prev === h ? prev : h))
  })

  const showMore = () => {
    if (!wsId || !view || view.status !== 'ready' || !view.cursor || view.more === 'loading') return
    const seq = ++listSeq.current
    const key = view.key
    const cursor = view.cursor
    // The bound the first page was read with — never a fresh `now`, or the cursor falls outside it.
    const from = view.from
    setList((prev) => (prev && prev.workspaceId === wsId && prev.key === key ? { ...prev, more: 'loading' } : prev))
    activityApi
      .ledger(wsId, { filter, channel, from, cursor, limit: LEDGER_PAGE_SIZE })
      .catch(() => null)
      .then((res) => {
        if (seq !== listSeq.current) return
        setList((prev) => {
          if (!prev || prev.workspaceId !== wsId || prev.key !== key) return prev
          if (!(res?.success && res.data && Array.isArray(res.data.items))) return { ...prev, more: 'error' }
          const page = res.data
          // De-dupe by id only where there IS one: two unreadable id-less rows are two rows.
          const seen = new Set(prev.items.map((i) => i.id).filter((id): id is string => !!id))
          const fresh = page.items.filter((i) => {
            if (!i.id) return true
            if (seen.has(i.id)) return false
            seen.add(i.id)
            return true
          })
          return {
            ...prev,
            items: [...prev.items, ...fresh],
            cursor: page.cursor,
            hasMore: !!page.hasMore,
            partial: !!page.partial,
            lastPageEmpty: page.items.length === 0 && !!page.hasMore,
            more: 'idle',
          }
        })
      })
  }

  // ── The drawer ───────────────────────────────────────────────────────────────
  // The URL (`?decision=<id>`) says which decision is open. `opened` records the workspace it
  // was opened under, so a workspace switch closes the drawer on the very render it happens.
  const [opened, setOpened] = useState<{ workspaceId: string; id: string } | null>(null)
  // The drawer's back stack (MAR-246): a person opened from a campaign returns to it. Names the
  // workspace it was built under and is read through that, like everything else here.
  const [trail, setTrail] = useState<{ workspaceId: string; ids: string[] } | null>(null)

  const setDecisionParam = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (id) next.set('decision', id)
          else next.delete('decision')
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  useEffect(() => {
    if (!wsId) return
    // A back stack built under another workspace is not scoped away — it is gone.
    if (trail && trail.workspaceId !== wsId) setTrail(null)
    if (!paramId) {
      setOpened((o) => (o ? null : o))
      return
    }
    if (opened && opened.id === paramId && opened.workspaceId !== wsId) {
      // The workspace changed under an open drawer: the decision belongs to the one we left.
      setOpened(null)
      setDecisionParam(null)
      return
    }
    if (!opened || opened.id !== paramId) setOpened({ workspaceId: wsId, id: paramId })
  }, [paramId, wsId, opened, trail, setDecisionParam])

  const openId = opened && wsId && opened.workspaceId === wsId && opened.id === paramId ? opened.id : null
  // An id we will not read (not a decision-id shape) is answered as not found, with no request.
  const readableOpenId = isReadableId(openId) ? openId : null

  const [detail, setDetail] = useState<DetailState | null>(null)
  const detailSeq = useRef(0)
  const [detailReload, setDetailReload] = useState(0)

  useEffect(() => {
    if (!wsId || !readableOpenId) return
    const openId = readableOpenId
    const seq = ++detailSeq.current
    setDetail({ workspaceId: wsId, id: openId, status: 'loading', data: null })
    activityApi
      .getDetail(wsId, openId)
      .catch(() => null)
      .then((res) => {
        if (seq !== detailSeq.current) return
        if (res?.success && res.data) {
          setDetail({ workspaceId: wsId, id: openId, status: 'ready', data: res.data })
        } else {
          // 404 (and 403): not a decision this workspace has. Anything else: the read failed.
          const notFound = res?.status === 404 || res?.status === 403
          setDetail({ workspaceId: wsId, id: openId, status: notFound ? 'not_found' : 'error', data: null })
        }
      })
  }, [wsId, readableOpenId, detailReload])

  const shownDetail = detail && openId && detail.workspaceId === wsId && detail.id === openId ? detail : null
  const row = readableOpenId ? items.find((i) => i.id === readableOpenId) ?? null : null
  const detailStatus = openId && !readableOpenId ? 'not_found' : shownDetail?.status ?? (openId ? 'loading' : null)
  // The row is stub content; the detail is authoritative. A failed detail read falls back to
  // the row the merchant clicked, as the Journal's drawer does — but only a row we HAVE.
  // A campaign line never falls back: its row is the night's title, not a decision, and drawn
  // as one it would offer a facts toggle, an Outcome and "Was this the right call?" for nobody.
  const openCampaign = isCampaignId(readableOpenId)
  const drawerActivity: DisplayActivity | null = openId
    ? ((shownDetail?.data as DisplayActivity | null) ??
      (detailStatus === 'not_found' || openCampaign ? null : row))
    : null
  const title = openId ? drawerTitle(row, shownDetail?.data ?? null) : ''

  // On screen until the slide-out ends, so the dock does not re-expand under a closing panel.
  const onDrawerExited = useDrawerPresence(!!openId)

  const backIds = trail && wsId && trail.workspaceId === wsId ? trail.ids : []

  // ── The Ask pin ──────────────────────────────────────────────────────────────
  // Journal's contract: pin only a real decision, name it exactly as the drawer does, release
  // only the pin THIS screen set, and release on close and on unmount.
  const { setEntity: setAskEntity, release: releaseAskEntity } = useAskEntity()
  const pinnedId = useRef<string | null>(null)
  const releasePin = useCallback(() => {
    if (!pinnedId.current) return
    releaseAskEntity('decision', pinnedId.current)
    pinnedId.current = null
  }, [releaseAskEntity])

  // Never pin an unreadable row: the server could not say what it is.
  const pinTarget =
    wsId &&
    readableOpenId &&
    drawerActivity &&
    drawerActivity.id === readableOpenId &&
    !(drawerActivity as LedgerItem).unreadable &&
    !row?.unreadable &&
    !isCampaignId(readableOpenId) &&
    isDecisionEntry(drawerActivity)
      ? { id: readableOpenId, label: title }
      : null

  useEffect(() => {
    if (!wsId) return
    if (pinTarget) {
      if (pinnedId.current && pinnedId.current !== pinTarget.id) releasePin()
      setAskEntity({ type: 'decision', id: pinTarget.id, label: pinTarget.label, workspaceId: wsId })
      pinnedId.current = pinTarget.id
    } else {
      releasePin()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId, pinTarget?.id, pinTarget?.label])

  const releaseRef = useRef(releasePin)
  useEffect(() => {
    releaseRef.current = releasePin
  })
  useEffect(() => () => releaseRef.current(), [])

  const openRow = (item: LedgerItem) => {
    const id = rowId(item)
    if (!wsId || !id) return
    // Publish in the same act as the open, from the row the merchant clicked — never for a row
    // the server could not read.
    setTrail(null)
    // A campaign line pins nothing: it is many decisions, not one Marva could be asked about.
    if (!item.unreadable && !isCampaignLine(item) && isDecisionEntry(item as DisplayActivity)) {
      if (pinnedId.current && pinnedId.current !== id) releasePin()
      setAskEntity({ type: 'decision', id, label: drawerTitle(item, null), workspaceId: wsId })
      pinnedId.current = id
    }
    setOpened({ workspaceId: wsId, id })
    setDecisionParam(id)
  }

  // The drawer binds Escape whether or not it is open, so a close with nothing open must not
  // touch the URL (a replace-navigation on every Escape keypress).
  const closeDrawer = useCallback(() => {
    if (!paramId) return
    detailSeq.current++
    releasePin()
    setTrail(null)
    setOpened(null)
    setDecisionParam(null)
  }, [paramId, releasePin, setDecisionParam])

  // A person in the campaign drawer opens their decision; the campaign goes on the back stack.
  const openFromDrawer = (id: string) => {
    if (!wsId || !openId || !isReadableId(id)) return
    setTrail({ workspaceId: wsId, ids: [...backIds, openId] })
    setOpened({ workspaceId: wsId, id })
    setDecisionParam(id)
  }
  const goBack = () => {
    const prev = backIds[backIds.length - 1]
    if (!wsId || !prev) return
    setTrail({ workspaceId: wsId, ids: backIds.slice(0, -1) })
    setOpened({ workspaceId: wsId, id: prev })
    setDecisionParam(prev)
  }

  // ── Feedback (POST …/activity/{id}/feedback) ─────────────────────────────────
  // Keyed `${workspace}:${entry}`, so an answer never shows under another workspace's entry.
  const [feedback, setFeedback] = useState<Record<string, FeedbackState>>({})
  const feedbackKey = wsId && openId ? `${wsId}:${openId}` : null
  // The one answer inside its undo window: what to send, where, and the timer that sends it.
  const pendingFeedback = useRef<{
    key: string
    workspaceId: string
    id: string
    payload: ActivityFeedback
    timer: ReturnType<typeof setTimeout>
    /** The answer this one replaces ("Change my answer"), restored as it was if she undoes. */
    previous?: FeedbackState
  } | null>(null)

  const sendFeedback = useCallback((key: string, workspaceId: string, id: string, payload: ActivityFeedback) => {
    setFeedback((f) => ({ ...f, [key]: { status: 'sending', payload } }))
    activityApi
      .submitFeedback(workspaceId, id, payload)
      .catch(() => null)
      .then((res) => {
        const settled: FeedbackState = { status: res?.success ? 'recorded' : 'error', payload }
        // This answer may already be the one a newer, still-undoable answer would restore: keep
        // that copy current, so an Undo restores what actually happened to it.
        const p = pendingFeedback.current
        if (p && p.key === key && p.previous?.payload === payload) p.previous = settled
        setFeedback((f) => {
          // A newer answer for this entry (or an Undo) owns the slot now.
          if (f[key]?.payload !== payload) return f
          return { ...f, [key]: settled }
        })
      })
  }, [])

  /** Send the held answer now — the drawer is closing or moving on, so the window is over. */
  const flushFeedback = useCallback(() => {
    const p = pendingFeedback.current
    if (!p) return
    clearTimeout(p.timer)
    pendingFeedback.current = null
    sendFeedback(p.key, p.workspaceId, p.id, p.payload)
  }, [sendFeedback])

  const submitFeedback = (payload: ActivityFeedback) => {
    // A campaign is many decisions: there is no one call to rate. The merchant's own change
    // (MAR-175) is not hers to be rated on either.
    if (!wsId || !openId || !readableOpenId || !feedbackKey || isCampaignId(openId) || isMerchantAction(drawerActivity)) return
    const held = pendingFeedback.current
    flushFeedback()
    // What this answer replaces: an earlier one still in its window is now being sent.
    const before: FeedbackState | undefined =
      held && held.key === feedbackKey ? { status: 'sending', payload: held.payload } : feedback[feedbackKey]
    const previous = before && before.status !== 'pending' ? before : undefined
    const key = feedbackKey
    const workspaceId = wsId
    const id = openId
    const timer = setTimeout(() => {
      if (pendingFeedback.current?.key !== key) return
      pendingFeedback.current = null
      sendFeedback(key, workspaceId, id, payload)
    }, FEEDBACK_UNDO_MS)
    pendingFeedback.current = { key, workspaceId, id, payload, timer, previous }
    setFeedback((f) => ({ ...f, [key]: { status: 'pending', payload } }))
  }

  const undoFeedback = () => {
    const p = pendingFeedback.current
    if (!p || p.key !== feedbackKey) return
    clearTimeout(p.timer)
    pendingFeedback.current = null
    setFeedback((f) => {
      const next = { ...f }
      // Undo takes back THIS answer only: one she had recorded before it ("Change my answer")
      // is still on the record, so it is still on the screen.
      if (p.previous) next[p.key] = p.previous
      else delete next[p.key]
      return next
    })
  }

  // A held answer is sent when the open entry changes (another row, the back stack, a workspace
  // switch, a close) and when the screen unmounts — never dropped because the merchant moved on.
  useEffect(() => {
    if (pendingFeedback.current && pendingFeedback.current.key !== feedbackKey) flushFeedback()
  }, [feedbackKey, flushFeedback])
  const flushRef = useRef(flushFeedback)
  useEffect(() => {
    flushRef.current = flushFeedback
  })
  useEffect(() => () => flushRef.current(), [])
  // A reload or a closed tab inside the window would otherwise drop the answer: send it as the
  // page goes (`pagehide` — fired where `unload` is not, and on bfcache entry).
  useEffect(() => {
    const onPageHide = () => flushRef.current()
    window.addEventListener('pagehide', onPageHide)
    return () => window.removeEventListener('pagehide', onPageHide)
  }, [])

  // ── Ask her about one she chose not to (a campaign line) ─────────────────────
  const openAsk = useOpenAsk()
  const askAboutSkips = () => {
    if (!wsId || !openId || !isLedgerCampaign(drawerActivity)) return
    const campaign = drawerActivity as unknown as LedgerCampaignDetail
    // The line itself is not a record Ask can prove; one of its decisions is — and Ask is shown
    // only that one. So the pin and the question name ONE decision she chose not to act on, in
    // this line, never the line's whole set of non-sends.
    const anchor = campaignSkipAnchor(campaign)
    if (!anchor || !isReadableId(anchor)) return
    if (pinnedId.current && pinnedId.current !== anchor) releasePin()
    setAskEntity({ type: 'decision', id: anchor, label: `A decision she chose not to act on in “${title}”`, workspaceId: wsId })
    pinnedId.current = anchor
    openAsk(`Why did she choose not to act on this one in “${title}”?`)
  }

  const receiptClass = drawerActivity ? receiptOf(drawerActivity)?.class : undefined
  const limitsLink =
    openId && receiptClass && LIMIT_CLASSES.has(receiptClass) && !isReplyDetail(drawerActivity) ? (
      <p className="mv-prose">
        <Link to={`/authority?tab=limits&from=ledger&decision=${encodeURIComponent(openId)}`}>
          The limits she works inside →
        </Link>
      </p>
    ) : null

  let placeholder: StoryPlaceholder | undefined = undefined
  if (openId && !drawerActivity) {
    // An `act_` id (MAR-175, Ask's receipt link) is the merchant's change, not her decision.
    const noun = openCampaign ? 'campaign' : openId.startsWith('act_') ? 'change' : 'decision'
    const eyebrow = openCampaign ? 'Campaign' : noun === 'change' ? 'You changed' : 'Decision'
    placeholder =
      detailStatus === 'not_found'
        ? {
            eyebrow,
            title: `She has no record of that ${noun}.`,
            body: (
              <p className="mv-prose">
                {noun === 'decision'
                  ? // A decision row expires after 90 days; the attribution row a Knowledge evidence
                    // link reads (MAR-390) lives a year, so such a link can outlive its receipt.
                    'It isn’t in this workspace’s ledger. Decisions age out of it after 90 days, and a link from something she still remembers — a belief’s evidence — can outlive one. Otherwise it belongs to another workspace, or the link is wrong.'
                  : 'It isn’t in this workspace’s ledger — it may belong to another workspace, or the link is wrong.'}
              </p>
            ),
          }
        : detailStatus === 'error'
          ? {
              eyebrow,
              title: `She couldn’t read that ${noun} just now.`,
              body: (
                <div>
                  <Button variant="ghost" size="sm" onClick={() => setDetailReload((n) => n + 1)}>
                    Try again
                  </Button>
                </div>
              ),
            }
          : { eyebrow, title: eyebrow, body: <LoadingState caption={`Reading the ${noun}…`} /> }
  }

  const clearFilters = () => {
    setFilter([])
    setChannel([])
  }

  // ── Render ───────────────────────────────────────────────────────────────────
  let body: React.ReactNode
  if (status === 'loading' && landedFor !== wsId) {
    // Today's opening beat, now Ledger's (MAR-309) — the first read of this workspace only.
    body = (
      <LoadingState
        variant="page"
        caption={isShadow ? 'Replaying the night in shadow' : 'Reading the night’s work'}
        steps={['Reading the ledger', 'Counting the decisions', 'Setting the night']}
      />
    )
  } else if (status === 'loading') {
    // A re-read (filter, channel, Try again): the page keeps its shape.
    body = <LoadingState caption="Reading the ledger…" />
  } else if (status === 'error') {
    body = (
      <ErrorState title="She couldn’t read the ledger just now." onRetry={() => setReload((n) => n + 1)}>
        Nothing is lost — it’s the read that failed.
      </ErrorState>
    )
  } else if (items.length === 0 && !view?.hasMore) {
    // Only the window was read: say nothing about what lies further back.
    const windowWords = WINDOW_WORDS[period]
    body = filtered ? (
      <EmptyState
        title={`Nothing matches that in ${windowWords}.`}
        action={
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        }
      />
    ) : (
      <EmptyState title={`Nothing in ${windowWords}.`}>
        Every decision she makes is written here. Widen the window above to look further back.
      </EmptyState>
    )
  } else {
    const note = view?.lastPageEmpty
      ? 'Nothing matching in the stretch she read — there’s more further back in this window.'
      : view?.partial
        ? 'She read as far back as she could this time — show more to keep going.'
        : null
    body = (
      <>
        {groupByDayPart(items, new Date(), timeZone).map((group, g) => (
          <div key={`${group.key}-${g}`} data-testid="ledger-daypart">
            {group.label && (
              <Eyebrow tone="muted" as="h3" data-testid="ledger-daypart-label">
                {group.label}
              </Eyebrow>
            )}
            <EntryList label={group.label ? `${group.label} — decisions, newest first` : 'Decisions, newest first'}>
              {group.items.map((item, index) => (
                <LedgerRow
                  key={item.id ?? `${item.timestamp}-${g}-${index}`}
                  item={item}
                  onOpen={openRow}
                  timeZone={timeZone}
                />
              ))}
            </EntryList>
          </div>
        ))}
        {view?.hasMore && (
          <LoadMore
            hasMore
            onLoad={showMore}
            // The failure is said in her words, so it rides in the note; the button retries.
            state={view.more === 'loading' ? 'loading' : 'idle'}
            label={view.more === 'error' ? 'Try again' : 'Show more'}
            note={
              note || view.more === 'error' ? (
                <>
                  {note}
                  {view.more === 'error' && (
                    <span role="alert">{note ? ' ' : ''}She couldn’t read further back just now.</span>
                  )}
                </>
              ) : undefined
            }
          />
        )}
      </>
    )
  }

  return (
    <div
      className="mv-canvas-wide"
      ref={pageRef}
      data-testid="ledger-page"
      // ds-dynamic-style: the page's own last settled height, held only while a re-read is in flight
      style={{ minHeight: rereading && settledHeight ? settledHeight : undefined }}
    >
      <PageHeader
        eyebrow="Ledger"
        title={
          period === '24h' ? (
            <>
              One night, <span className="mv-em">while you slept.</span>
            </>
          ) : (
            <>
              {listHeading(period)}, <span className="mv-em">one line each.</span>
            </>
          )
        }
        sub="Every decision she made, one line each and newest first — the ones where she acted, and the ones where she chose not to."
      />

      <AttributionStrip workspaceId={wsId} period={period} onPeriodChange={setPeriod} />
      <LedgerBanners isShadow={isShadow} />
      <WorthALook workspaceId={wsId} period={period} onOpen={openRow} timeZone={timeZone} />

      <Section
        title={listHeading(period)}
        aside={
          filtered ? (
            <Button variant="link" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : undefined
        }
      >
        <FilterBar>
          <FilterMenu
            field="action"
            allLabel="Everything"
            options={ACTION_OPTIONS}
            selected={filter}
            onChange={(next) => setFilter(inMenuOrder(ACTION_OPTIONS, next))}
          />
          <FilterMenu
            field="channel"
            allLabel="Any channel"
            options={CHANNEL_OPTIONS}
            selected={channel}
            onChange={(next) => setChannel(inMenuOrder(CHANNEL_OPTIONS, next))}
          />
        </FilterBar>

        {body}
      </Section>

      <StoryDrawer
        activity={drawerActivity}
        loading={!!drawerActivity && detailStatus === 'loading'}
        title={drawerActivity ? title : undefined}
        placeholder={placeholder}
        extra={limitsLink}
        feedback={feedbackKey ? feedback[feedbackKey] : undefined}
        onClose={closeDrawer}
        onExited={onDrawerExited}
        onFeedback={submitFeedback}
        onUndoFeedback={undoFeedback}
        onAskAboutSkips={openCampaign ? askAboutSkips : undefined}
        onOpenActivity={openFromDrawer}
        onBack={backIds.length > 0 ? goBack : undefined}
        scope={wsId}
      />
    </div>
  )
}
